import { useState, useMemo } from "react";
import { type AuthUser } from "wasp/auth";
import {
  getAdminPropFirmTemplates,
  createPropFirmTemplate,
  updatePropFirmTemplate,
  deletePropFirmTemplate,
  useQuery,
  useAction,
} from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import ReferentialImportDialog from "./ReferentialImportDialog";
import Breadcrumb from "../../layout/Breadcrumb";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { Badge } from "../../../client/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../client/components/ui/card";
import { useToast } from "../../../client/hooks/use-toast";
import {
  type AccountTypeForm,
  emptyAccountType,
  accountTypeToForm,
  tierFormToData,
  drawdownLockFormToData,
  phasesByTierId,
} from "./propFirmTierForm";
import { collectTierProblems } from "./propFirmFormValidation";
import { guardPhaseSave, serializeTemplateState } from "./propFirmPhaseGuard";
import { AccountTypeEditor } from "./AccountTypeEditor";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Building2,
  Layers,
  Save,
  X,
  Upload,
} from "lucide-react";


// Plateformes proposees dans le formulaire. La colonne reste du texte libre
// cote base (l'import CSV du referentiel accepte d'autres valeurs) : cette
// liste ne restreint que la saisie manuelle de l'admin.
const PLATFORM_OPTIONS = ["tradovate", "rithmic", "ninjatrader"];


// ─── PropFirmForm ─────────────────────────────────────────────────────────────

function PropFirmForm({
  initial,
  onSave,
  onSaveInline,
  onCancel,
  onRefresh,
  isSaving,
}: {
  initial?: any;
  onSave: (data: any) => void;
  /**
   * Sauvegarde le template SANS quitter le formulaire, en laissant remonter
   * l'erreur. Distincte d'`onSave`, qui referme la vue et avale son erreur dans
   * son propre toast : appeler `onSave` avant d'ecrire une phase renverrait
   * l'admin a la liste au milieu de son edition. Absente en mode creation, ou
   * aucune phase n'est editable.
   */
  onSaveInline?: (data: any) => Promise<void>;
  onCancel: () => void;
  onRefresh: () => void;
  isSaving: boolean;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(initial?.name ?? "");
  const [color, setColor] = useState(initial?.color ?? "#71717a");
  const [textColor, setTextColor] = useState(initial?.textColor ?? "#ffffff");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");
  const [commissionPerContract, setCommissionPerContract] = useState<string>(
    initial?.commissionPerContract != null ? String(initial.commissionPerContract) : ""
  );
  const [supportedPlatforms, setSupportedPlatforms] = useState<string[]>(
    initial?.supportedPlatforms ?? []
  );
  // "wallet" : les sorties d'argent des comptes funded partent vers le wallet
  // de la prop firm (TPT), pas en payout. Voir walletPayoutRouting.ts.
  const [payoutDestination, setPayoutDestination] = useState<"direct" | "wallet">(
    initial?.payoutDestination === "wallet" ? "wallet" : "direct"
  );
  // Une prop firm creee depuis l'admin est officielle par defaut. En edition on
  // reprend le statut existant, pour que la case reflete la base et ne
  // deplace rien par inadvertance.
  const [isSystem, setIsSystem] = useState<boolean>(initial ? initial.isSystem === true : true);

  type SymbolCommissionRow = { prefix: string; rate: string };

  const [symbolCommissionRows, setSymbolCommissionRows] = useState<SymbolCommissionRow[]>(
    () => {
      if (initial?.symbolCommissions && typeof initial.symbolCommissions === "object") {
        return Object.entries(initial.symbolCommissions as Record<string, number>).map(
          ([prefix, rate]) => ({ prefix, rate: String(rate) })
        );
      }
      return [];
    }
  );

  const [accountTypes, setAccountTypes] = useState<AccountTypeForm[]>(
    initial?.accountTypes?.length
      ? initial.accountTypes.map(accountTypeToForm)
      : [emptyAccountType(0)],
  );

  /**
   * Le résumé n'apparaît qu'après une première tentative d'enregistrement : le
   * formulaire vierge est intégralement en faute, l'afficher d'emblée
   * reviendrait à accueillir l'admin par une liste rouge.
   */
  const [submitAttempted, setSubmitAttempted] = useState(false);

  /**
   * Instantané de l'état tel qu'il a été chargé, pour détecter une saisie non
   * enregistrée. Figé à la CRÉATION du composant : le parent remonte le
   * formulaire (`key={editingTemplate.id}`) quand on change de template, donc
   * cet instantané correspond toujours au template affiché.
   *
   * ⚠️ Ne PAS recalculer à chaque rendu depuis `initial` : `initial` est
   * réévalué après chaque mutation de phase, si bien que l'instantané
   * rattraperait la saisie en cours et `isDirty` retomberait à `false` — la
   * garde ne protégerait plus rien.
   */
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify(serializeTemplateState(initial)),
  );

  /**
   * Phases par palier, lues depuis le template FRAIS et non depuis l'état du
   * formulaire : `accountTypes` est un brouillon initialisé une fois au
   * montage, il ne verrait jamais une phase créée ensuite. `initial` suit le
   * cache de la query, que Wasp invalide après chaque écriture de phase, donc
   * la liste se met à jour seule. Détail dans `phasesByTierId`.
   */
  const phasesByTier = useMemo(() => phasesByTierId(initial), [initial]);

  // POURQUOI cette collecte plutôt que des `return` : `handleSubmit` faisait
  // trois `return` MUETS (nom du template, nom/slug du type de compte, label
  // du palier). L'admin cliquait « Enregistrer », rien ne se passait, aucune
  // explication, aucune indication du champ fautif. Pire, le bail-out se
  // faisait au PREMIER problème : corriger le champ signalé révélait le
  // suivant, une correction à la fois. On collecte donc TOUS les blocages en
  // une passe, on les affiche en tête de formulaire, et les inputs fautifs
  // portent un liséré rouge.
  //
  // POURQUOI un memo DÉRIVÉ et non un instantané pris au submit : un
  // instantané se périme. L'admin corrige un des trois problèmes listés, et le
  // bandeau continue d'annoncer « 3 champ(s) » en citant celui qu'il vient de
  // réparer, jusqu'au prochain clic. Une liste intitulée « empêchent
  // l'enregistrement » qui contient un élément déjà corrigé est exactement la
  // classe de retour trompeur que ce commit existe pour supprimer. Le coût est
  // nul : `collectTierProblems` tourne déjà à chaque rendu dans chaque
  // `TierEditor`.
  //
  // La sévérité (bloquant vs avertissement) est décidée par
  // `collectTierProblems`, qui relaie aussi `validateTierRules` : un palier
  // déclarant un drawdown suiveur sans montant est accepté par la base mais
  // IGNORÉ par le moteur, qui retombe sur une règle statique plus permissive,
  // si bien que le compte n'est jamais détecté en breach.
  const blockingProblems = useMemo(() => {
    const collected: { tierLabel: string; message: string }[] = [];

    if (!name.trim()) {
      collected.push({ tierLabel: "", message: "Le nom de la prop firm est obligatoire." });
    }
    for (const at of accountTypes) {
      if (!at.name.trim() || !at.slug.trim()) {
        collected.push({
          tierLabel: at.name || "(type sans nom)",
          message: "Nom et slug du type de compte sont obligatoires.",
        });
      }
      for (const t of at.tiers) {
        for (const p of collectTierProblems(t, at.pricingModel)) {
          if (p.blocking) {
            collected.push({ tierLabel: t.label || "(palier sans nom)", message: p.message });
          }
        }
      }
    }

    return collected;
  }, [name, accountTypes]);

  // POURQUOI le payload est DÉRIVÉ et non construit dans `handleSubmit` : il a
  // désormais deux appelants. Le submit du formulaire, et la sauvegarde
  // implicite déclenchée avant l'écriture d'une phase (voir
  // `propFirmPhaseGuard.ts`). Les deux DOIVENT envoyer exactement les mêmes
  // données : si la sauvegarde implicite omettait un champ, ajouter une phase
  // écraserait ce champ en base — une autre façon de perdre le travail de
  // l'admin, plus sournoise que celle qu'on corrige.
  const buildPayload = () => ({
    name: name.trim(),
    color,
    textColor,
    // N'est envoye QUE s'il change reellement. Le renvoyer a chaque
    // enregistrement ferait rejouer la bascule (et le renommage de slug qui
    // l'accompagne) a chaque sauvegarde ordinaire. A la creation, le serveur
    // deduit le statut du role de l'auteur : inutile de le transmettre.
    ...(initial && isSystem !== (initial.isSystem === true) ? { isSystem } : {}),
    logoUrl: logoUrl.trim() !== "" ? logoUrl.trim() : null,
    supportedPlatforms,
    commissionPerContract: commissionPerContract !== "" ? parseFloat(commissionPerContract) : null,
    payoutDestination,
    symbolCommissions: symbolCommissionRows.length > 0
      ? Object.fromEntries(
          symbolCommissionRows
            .filter(r => r.prefix.trim() !== "" && r.rate.trim() !== "")
            .map(r => [r.prefix.trim().toUpperCase(), parseFloat(r.rate)])
        )
      : null,
    accountTypes: accountTypes.map((at, atIdx) => ({
      id: at.id,
      name: at.name,
      slug: at.slug,
      sortOrder: atIdx,
      isActive: at.isActive,
      pricingModel: at.pricingModel,
      ...drawdownLockFormToData(at),
      tiers: at.tiers.map(tierFormToData),
    })),
  });

  /**
   * Le formulaire porte-t-il une saisie jamais envoyée au serveur ?
   *
   * Comparé sur la PROJECTION des deux états (voir `serializeTemplateState`) et
   * non sur les objets bruts, qui n'ont pas la même forme de part et d'autre.
   * Un template en cours de CRÉATION n'a pas de `initial` : il est modifié par
   * construction, mais ses paliers n'ont pas encore d'`id`, donc aucune phase
   * n'y est éditable et la garde ne peut pas se déclencher.
   */
  const isDirty = useMemo(
    () => JSON.stringify(serializeTemplateState(buildPayload())) !== savedSnapshot,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, color, textColor, logoUrl, commissionPerContract, supportedPlatforms,
     symbolCommissionRows, accountTypes, savedSnapshot, isSystem, payoutDestination],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);

    // Le TOAST reste lié à la tentative, pas à la saisie : le résumé se met à
    // jour en continu, mais surgir en notification à chaque frappe serait
    // insupportable.
    if (blockingProblems.length > 0) {
      toast({
        title: `${blockingProblems.length} champ(s) a corriger`,
        description: blockingProblems[0].message,
        variant: "destructive",
      });
      return;
    }

    onSave(buildPayload());
  };

  /**
   * Sauvegarde le template AVANT qu'une phase ne soit écrite en base.
   *
   * Appelée depuis le sous-arbre (`AccountTypeEditor` → `TierEditor` →
   * `PhaseEditor`). Renvoie `false` si l'écriture de la phase doit être
   * abandonnée : soit le formulaire est bloqué, soit la sauvegarde a échoué.
   * Dans les deux cas l'appelant ne doit RIEN écrire, car le refresh qui suit
   * l'écriture d'une phase réinjecte le template du serveur et effacerait la
   * saisie en cours.
   */
  const saveBeforePhaseWrite = async (): Promise<boolean> => {
    const guard = guardPhaseSave({
      hasUnsavedTemplateChanges: isDirty,
      blockingProblems,
    });

    if (guard.action === "save-phase-only") return true;

    if (guard.action === "block") {
      setSubmitAttempted(true);
      toast({
        title: `${guard.blockingCount} champ(s) a corriger avant d'editer les phases`,
        description: guard.reason,
        variant: "destructive",
      });
      return false;
    }

    // Sans `onSaveInline` (mode creation), aucun palier n'a d'`id`, donc aucune
    // phase n'est editable : ce chemin est inatteignable. On refuse par surete
    // plutot que d'appeler `onSave`, qui refermerait le formulaire.
    if (!onSaveInline) return false;

    const payload = buildPayload();
    try {
      await onSaveInline(payload);
      // Le travail est désormais en base : il devient la nouvelle référence.
      // Sans cette ligne, `isDirty` resterait vrai et CHAQUE phase suivante
      // redéclencherait une sauvegarde complète du template.
      setSavedSnapshot(JSON.stringify(serializeTemplateState(payload)));
      return true;
    } catch {
      // `onSave` affiche déjà son propre toast d'erreur. On se contente
      // d'interrompre : écrire la phase malgré l'échec perdrait la saisie.
      return false;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {submitAttempted && blockingProblems.length > 0 && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive mb-1">
            {blockingProblems.length} champ(s) empêchent l'enregistrement
          </p>
          <ul className="text-xs text-destructive/90 space-y-0.5 list-disc pl-4">
            {blockingProblems.map((p, i) => (
              <li key={i}>{p.tierLabel ? `${p.tierLabel} : ` : ""}{p.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Template info */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1">
          <label className="text-sm text-muted-foreground block mb-1">
            Nom<span className="text-destructive ml-0.5">*</span>
          </label>
          {/*
            PAS de `required` natif ici. Il interceptait le submit AVANT
            `handleSubmit`, si bien que le nom etait le seul champ bloquant
            incapable d'apparaitre dans le resume rouge. Les deux mecanismes
            etaient en plus en desaccord sur la definition de « vide » : le
            natif accepte une suite d'espaces, `!name.trim()` non. Le
            collecteur JS est donc l'unique source de verite, comme pour tous
            les autres champs bloquants.
          */}
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="FTMO, Apex..." />
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">Couleur badge</label>
          <div className="flex gap-2">
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="h-9 w-12 rounded border" />
            <Input value={color} onChange={e => setColor(e.target.value)} className="font-mono text-xs" />
          </div>
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">Couleur texte</label>
          <div className="flex gap-2">
            <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} className="h-9 w-12 rounded border" />
            <Input value={textColor} onChange={e => setTextColor(e.target.value)} className="font-mono text-xs" />
          </div>
        </div>
        <div className="col-span-3">
          <label className="text-sm text-muted-foreground block mb-1">
            Logo (chemin local ou URL)
          </label>
          <div className="flex items-center gap-2">
            <Input
              value={logoUrl}
              onChange={e => setLogoUrl(e.target.value)}
              placeholder="/logos/propfirms/apex.png"
              className="font-mono text-xs"
            />
            {logoUrl.trim() !== "" && (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded border bg-white/90 p-1">
                <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
              </span>
            )}
          </div>
        </div>
        <div className="col-span-3">
          <label className="text-sm text-muted-foreground block mb-1">
            Plateformes supportées
          </label>
          <div className="flex flex-wrap items-center gap-3">
            {PLATFORM_OPTIONS.map(p => (
              <label key={p} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={supportedPlatforms.includes(p)}
                  onChange={e =>
                    setSupportedPlatforms(
                      e.target.checked
                        ? [...supportedPlatforms, p]
                        : supportedPlatforms.filter(x => x !== p)
                    )
                  }
                />
                {p}
              </label>
            ))}
          </div>
        </div>
        <div className="col-span-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={isSystem}
              onChange={e => setIsSystem(e.target.checked)}
            />
            <span>
              Prop firm officielle (Système)
              <span className="block text-xs text-muted-foreground">
                Visible par tous les utilisateurs. Décochée, elle devient une prop firm
                personnelle, visible de vous seul.
              </span>
            </span>
          </label>
          {initial && isSystem !== (initial.isSystem === true) && (
            <p className="mt-1 text-xs text-amber-500">
              {isSystem
                ? "À l'enregistrement, cette prop firm deviendra visible par tous les utilisateurs."
                : "À l'enregistrement, cette prop firm ne sera plus visible que par vous."}
            </p>
          )}
        </div>
      </div>

      {/* Preview + commission */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Aperçu badge :</span>
          <span
            className="px-2 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: color, color: textColor }}
          >
            {name || "PropFirm"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground whitespace-nowrap">Commission/contrat ($/RT)</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={commissionPerContract}
            onChange={e => setCommissionPerContract(e.target.value)}
            placeholder="ex: 1.04"
            className="w-28 h-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground whitespace-nowrap">Sorties des comptes funded</label>
          <select
            value={payoutDestination}
            onChange={e => setPayoutDestination(e.target.value as "direct" | "wallet")}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="direct">Payout (versé au trader)</option>
            <option value="wallet">Transfert vers le wallet (TPT)</option>
          </select>
        </div>
      </div>

      {/* Symbol commissions */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm text-muted-foreground">Commissions par symbole (surcharge par préfixe)</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSymbolCommissionRows([...symbolCommissionRows, { prefix: "", rate: "" }])}
          >
            <Plus className="w-3 h-3 mr-1" /> Ajouter
          </Button>
        </div>
        {symbolCommissionRows.length === 0 && (
          <p className="text-xs text-muted-foreground/60 italic">Aucune surcharge : taux unique appliqué à tous les symboles</p>
        )}
        {symbolCommissionRows.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              placeholder="ex: NQ"
              value={row.prefix}
              onChange={e => setSymbolCommissionRows(symbolCommissionRows.map((r, j) => j === i ? { ...r, prefix: e.target.value.toUpperCase() } : r))}
              className="w-24 h-8 text-sm font-mono"
            />
            <span className="text-muted-foreground text-sm">→</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="ex: 5.75"
              value={row.rate}
              onChange={e => setSymbolCommissionRows(symbolCommissionRows.map((r, j) => j === i ? { ...r, rate: e.target.value } : r))}
              className="w-24 h-8 text-sm"
            />
            <span className="text-xs text-muted-foreground">$/RT</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSymbolCommissionRows(symbolCommissionRows.filter((_, j) => j !== i))}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>

      {/* Account Types */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            Types de comptes ({accountTypes.length})
          </h3>
          <Button
            type="button"
            variant="outline"
            onClick={() => setAccountTypes([...accountTypes, emptyAccountType(accountTypes.length)])}
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter un type
          </Button>
        </div>
        {accountTypes.map((at, i) => (
          <AccountTypeEditor
            /*
              Cle STABLE, pas l'index : meme motif que la liste de paliers dans
              `AccountTypeEditor`. SUPPRIMER un type de compte decale tous les
              suivants ; avec `key={i}`, React rattacherait l'etat local d'un
              type (replie/deplie, et les formulaires de phase en cours de
              saisie de chaque `PhaseEditor` du sous-arbre) a son VOISIN. Repli
              sur l'index si la cle manque : `clientKey` est optionnel dans le
              type, la cle ne doit jamais valoir `undefined`.
            */
            key={at.clientKey ?? i}
            at={at}
            index={i}
            onChange={updated => setAccountTypes(accountTypes.map((x, j) => j === i ? updated : x))}
            onDelete={() => setAccountTypes(accountTypes.filter((_, j) => j !== i))}
            onRefresh={onRefresh}
            onBeforePhaseWrite={saveBeforePhaseWrite}
            phasesByTierId={phasesByTier}
          />
        ))}
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="w-4 h-4 mr-1" /> Annuler
        </Button>
        <Button type="submit" disabled={isSaving}>
          <Save className="w-4 h-4 mr-1" /> {initial ? "Mettre à jour" : "Créer"}
        </Button>
      </div>
    </form>
  );
}

// ─── PropFirmCard ─────────────────────────────────────────────────────────────

function PropFirmCard({
  template,
  onEdit,
  onDelete,
}: {
  template: any;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className={template.isGrayed ? "opacity-50" : ""}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className="px-2 py-0.5 rounded text-xs font-bold"
              style={{ backgroundColor: template.color, color: template.textColor }}
            >
              {template.name}
            </span>
            <Badge variant={template.isSystem ? "default" : "outline"} className="text-xs">
              {template.isSystem ? "Système" : "Custom"}
            </Badge>
            {template.isGrayed && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded border border-border text-muted-foreground bg-muted">
                {template.grayedReason ?? "Non disponible"}
              </span>
            )}
            <span className="text-xs text-muted-foreground">{template.accountTypes?.length ?? 0} type(s)</span>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-7 px-2">
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 px-2">
              <Pencil className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 px-2 text-destructive">
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="px-4 pb-4 pt-0 space-y-2">
          {template.accountTypes?.map((at: any) => {
            const assetClassColors: Record<string, string> = {
              futures: "bg-blue-500/20 text-blue-400",
              cfd: "bg-yellow-500/20 text-yellow-400",
              forex: "bg-green-500/20 text-green-400",
            };
            return (
            <div key={at.id} className="border border-border rounded p-2">
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-3 h-3 text-primary" />
                <span className="text-sm font-medium">{at.name}</span>
                <span className="text-xs text-muted-foreground font-mono">{at.slug}</span>
                {at.assetClass && (
                  <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${assetClassColors[at.assetClass] ?? "bg-muted text-muted-foreground"}`}>
                    {at.assetClass}
                  </span>
                )}
                {!at.isActive && <Badge variant="outline" className="text-xs">Inactif</Badge>}
                {at.isDiscontinued && <span className="text-xs text-pnlNegative ml-1">• Discontinué</span>}
              </div>
              <div className="grid grid-cols-3 gap-1">
                {at.tiers?.map((tier: any) => (
                  <div key={tier.id} className="bg-muted rounded px-2 py-1 text-xs">
                    <p className="font-medium">{tier.label}</p>
                    {tier.profitTargetPercent && <p className="text-muted-foreground">Target: {tier.profitTargetPercent}%</p>}
                    {tier.maxTotalLossPercent && <p className="text-muted-foreground">Max loss: {tier.maxTotalLossPercent}%</p>}
                    {tier.hasTrailingDrawdown && <p className="text-muted-foreground">Trailing: {tier.trailingDrawdownPercent}%</p>}
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminPropFirmsPage = ({ user }: { user: AuthUser }) => {
  const { data: templates, isLoading, refetch } = useQuery(getAdminPropFirmTemplates);
  const createTemplateFn = useAction(createPropFirmTemplate);
  const updateTemplateFn = useAction(updatePropFirmTemplate);
  const deleteTemplateFn = useAction(deletePropFirmTemplate);
  const { toast } = useToast();

  const [view, setView] = useState<"list" | "create" | "edit">("list");
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const filtered = (templates ?? []).filter((t: any) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const systemTemplates = filtered.filter((t: any) => t.isSystem);
  const customTemplates = filtered.filter((t: any) => !t.isSystem);

  const handleCreate = async (data: any) => {
    setIsSaving(true);
    try {
      await createTemplateFn(data);
      toast({ title: "PropFirm créée avec succès" });
      setView("list");
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: any) => {
    setIsSaving(true);
    try {
      await updateTemplateFn({ id: editingTemplate.id, ...data });
      toast({ title: "PropFirm mise à jour" });
      setView("list");
      setEditingTemplate(null);
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Sauvegarde le template en RESTANT dans le formulaire, et en laissant
   * remonter l'erreur a l'appelant.
   *
   * Utilisee avant l'ecriture d'une phase : `handleUpdate` ne convient pas, il
   * referme la vue (`setView("list")`) et avale son erreur, si bien que
   * l'appelant ne saurait pas s'il peut ecrire la phase sans risque.
   */
  const handleUpdateInline = async (data: any) => {
    setIsSaving(true);
    try {
      await updateTemplateFn({ id: editingTemplate.id, ...data });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
      throw e;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Supprimer "${name}" ? Cette action est irréversible.`)) return;
    try {
      await deleteTemplateFn({ id });
      toast({ title: `"${name}" supprimé` });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    }
  };

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName="PropFirm Templates" />

      <div className="space-y-6">
        {view === "list" && (
          <>
            <div className="flex items-center justify-between gap-4">
              <Input
                placeholder="Rechercher une propfirm..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="max-w-xs"
              />
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" /> Importer les CSV
                </Button>
                <Button onClick={() => setView("create")}>
                  <Plus className="w-4 h-4 mr-2" /> Nouvelle PropFirm
                </Button>
              </div>
            </div>

            {isLoading ? (
              <p className="text-muted-foreground text-sm">Chargement...</p>
            ) : (
              <div className="space-y-6">
                {systemTemplates.length > 0 && (
                  <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Prop Firms Système ({systemTemplates.length})
                    </h2>
                    {systemTemplates.map((t: any) => (
                      <PropFirmCard
                        key={t.id}
                        template={t}
                        onEdit={() => { setEditingTemplate(t); setView("edit"); }}
                        onDelete={() => handleDelete(t.id, t.name)}
                      />
                    ))}
                  </div>
                )}

                {customTemplates.length > 0 && (
                  <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> PropFirms Custom ({customTemplates.length})
                    </h2>
                    {customTemplates.map((t: any) => (
                      <PropFirmCard
                        key={t.id}
                        template={t}
                        onEdit={() => { setEditingTemplate(t); setView("edit"); }}
                        onDelete={() => handleDelete(t.id, t.name)}
                      />
                    ))}
                  </div>
                )}

                {filtered.length === 0 && (
                  <p className="text-muted-foreground text-sm">Aucune propfirm trouvée.</p>
                )}
              </div>
            )}
          </>
        )}

        {view === "create" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Nouvelle PropFirm
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PropFirmForm
                onSave={handleCreate}
                onCancel={() => setView("list")}
                onRefresh={() => {}}
                isSaving={isSaving}
              />
            </CardContent>
          </Card>
        )}

        {view === "edit" && editingTemplate && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5" /> Modifier : {editingTemplate.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PropFirmForm
                key={editingTemplate.id}
                initial={
                  // Sync the latest data from the query cache after phase mutations
                  (templates ?? []).find((t: any) => t.id === editingTemplate.id) ?? editingTemplate
                }
                onSave={handleUpdate}
                onSaveInline={handleUpdateInline}
                onCancel={() => { setView("list"); setEditingTemplate(null); }}
                onRefresh={() => {
                  const fresh = (templates ?? []).find((t: any) => t.id === editingTemplate.id);
                  if (fresh) setEditingTemplate(fresh);
                }}
                isSaving={isSaving}
              />
            </CardContent>
          </Card>
        )}
      </div>

      <ReferentialImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => refetch()}
      />
    </DefaultLayout>
  );
};

export default AdminPropFirmsPage;
